import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ApiService} from '../api/api.service';
import {ShapefileImport, ShapefileImportExisting, ShapefileImportPart} from '../../classes/shapefile-import.class';

/**
 * The original shapefiles (zip) that pushpin parts are imported from, used to return the shapefile to the
 * customer with the status/CROW per part added (see PushpinShapeExportComponent).
 */
@Injectable({
    providedIn: 'root'
})
export class ShapefileImportService {

    constructor(private apiService: ApiService) {
    }

    upload(file: File, inspectionId: number, referenceField: string): Observable<ShapefileImport> {
        return this.apiService.makeFileRequest$<ShapefileImport>('shapefile-imports', file, {
            inspection_id: inspectionId,
            reference_field: referenceField ?? ''
        })
            .pipe(map(response => {
                if (!response?.success || !response.data?.id) {
                    throw new Error(response?.message || 'Het shapefile kon niet worden opgeslagen');
                }
                return response.data;
            }));
    }

    getImports(inspectionId: number): Observable<ShapefileImport[]> {
        return this.apiService.getCallData$<ShapefileImport[]>('shapefile-imports', {inspection_id: inspectionId});
    }

    getExisting(inspectionId: number, fileHash: string): Observable<ShapefileImportExisting> {
        return this.apiService.getCallData$<ShapefileImportExisting>('shapefile-imports/existing', {
            inspection_id: inspectionId,
            file_hash: fileHash ?? ''
        });
    }

    /** SHA-256 of the file as hex, the same as the server calculates; null when the browser can not calculate it */
    async hashFile(buffer: ArrayBuffer): Promise<string | null> {
        if (!window.crypto?.subtle) {
            return null; // only available on https and localhost
        }
        const hash = await window.crypto.subtle.digest('SHA-256', buffer);
        return Array.from(new Uint8Array(hash)).map(byte => byte.toString(16).padStart(2, '0')).join('');
    }

    getOriginal(shapefileImportId: number): Observable<Blob> {
        return this.apiService.getBlobCall(`shapefile-imports/download/${shapefileImportId}`);
    }

    getParts(shapefileImportId: number): Observable<ShapefileImportPart[]> {
        return this.apiService.getCallData$<ShapefileImportPart[]>(`shapefile-imports/parts/${shapefileImportId}`);
    }
}
